// /admin/inteligencia-mercado
// Visión de mercado de MercadoLibre. Bloque A: mis productos publicados vs el mercado.
// Usa la extensión Chrome "Limpack — Precios" para scrapear ML sin costo de API/créditos.
//
// Flujo:
//   1. Lista mis productos publicados en ML (publicar_en_ml=true)
//   2. Botón "Escanear mercado" dispara la extensión (postMessage LIMPACK_BUSCAR_PRECIOS)
//   3. Por cada producto la extensión devuelve stats de ML (mediana/min/max/cantidad + match exacto)
//   4. guardar-precios-producto persiste en productos.ml_stats + precio_mercado y calcula posición
//   5. La tabla muestra: mi precio vs mercado, % diff, semáforo, # competidores
//
// Bloques B (categorías calientes) y C (huecos de catálogo) se suman después.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => n == null ? '—' : `$${Math.round(Number(n)).toLocaleString('es-UY')}`
const fmtFechaRel = (d) => {
  if (!d) return 'nunca'
  const min = Math.floor((Date.now() - new Date(d).getTime()) / 60000)
  if (min < 1) return 'recién'
  if (min < 60) return `hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `hace ${h}h`
  const dias = Math.floor(h / 24)
  return `hace ${dias}d`
}

// Meta visual por posición de precio (la calcula guardar-precios-producto)
const POSICION_META = {
  competitivo: { label: 'Competitivo', bg: '#DCFCE7', color: '#166534', dot: '#16A34A', icon: '✓' },
  en_linea:    { label: 'En línea',    bg: '#DBEAFE', color: '#1E40AF', dot: '#3B82F6', icon: '≈' },
  caro:        { label: 'Caro',        bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B', icon: '▲' },
  muy_caro:    { label: 'Muy caro',    bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444', icon: '▲▲' },
  sin_datos:   { label: 'Sin datos',   bg: '#F1F5F9', color: '#64748B', dot: '#94A3B8', icon: '—' },
  sin_precio:  { label: 'Sin precio',  bg: '#F1F5F9', color: '#64748B', dot: '#94A3B8', icon: '—' },
}

export default function InteligenciaMercado() {
  const [productos, setProductos] = useState([])
  const [loading, setLoading] = useState(true)
  const [extInstalada, setExtInstalada] = useState(false)
  const [seleccion, setSeleccion] = useState(new Set())
  const [ejecutando, setEjecutando] = useState(false)
  const [progreso, setProgreso] = useState(null)
  const [msg, setMsg] = useState(null)
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => { cargar() }, [])

  // Detectar extensión Chrome (mismo patrón que AnalisisCatalogoDetalle)
  useEffect(() => {
    function onMessage(e) {
      if (e.source !== window) return
      if (e.data?.type === 'LIMPACK_EXTENSION_READY') setExtInstalada(true)
    }
    window.addEventListener('message', onMessage)
    const pings = [0, 300, 800, 2000, 4000].map(delay =>
      setTimeout(() => window.postMessage({ type: 'LIMPACK_EXTENSION_CHECK' }, '*'), delay)
    )
    return () => {
      window.removeEventListener('message', onMessage)
      pings.forEach(clearTimeout)
    }
  }, [])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase
      .from('productos')
      .select('id, sku, nombre, marca, presentacion, categoria, precio_publico, precio_mercado, ml_precio, ml_status, ml_stats, ml_stats_actualizado_at')
      .eq('publicar_en_ml', true).eq('activo', true).is('deleted_at', null)
      .order('marca').order('nombre')
    setProductos(data || [])
    setLoading(false)
  }

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase()
    if (!q) return productos
    return productos.filter(p =>
      [p.sku, p.nombre, p.marca, p.categoria].filter(Boolean)
        .map(String).map(s => s.toLowerCase()).join(' ').includes(q))
  }, [productos, busqueda])

  // KPIs resumen
  const resumen = useMemo(() => {
    const r = { total: productos.length, escaneados: 0, competitivo: 0, en_linea: 0, caro: 0, muy_caro: 0, sin_datos: 0, sin_escanear: 0 }
    for (const p of productos) {
      const pos = p.ml_stats?.posicion
      if (!p.ml_stats_actualizado_at) { r.sin_escanear++; continue }
      r.escaneados++
      if (pos && r[pos] != null) r[pos]++
      else r.sin_datos++
    }
    return r
  }, [productos])

  function toggleSel(id) {
    setSeleccion(prev => {
      const s = new Set(prev)
      if (s.has(id)) s.delete(id); else s.add(id)
      return s
    })
  }
  function toggleTodos() {
    if (seleccion.size === filtrados.length) setSeleccion(new Set())
    else setSeleccion(new Set(filtrados.map(p => p.id)))
  }

  async function escanear(objetivos) {
    if (!extInstalada) {
      setMsg({ tipo: 'err', txt: 'La extensión "Limpack — Precios" no está instalada o no cargó todavía.' })
      return
    }
    if (objetivos.length === 0) { setMsg({ tipo: 'err', txt: 'Elegí al menos un producto.' }); return }
    const segundos = Math.ceil(objetivos.length * 8)
    if (!confirm(`¿Escanear el mercado de ${objetivos.length} producto(s)?\n\nLa extensión abre pestañas ocultas en ML y lee los precios. ~${segundos}s total. Sin costo.`)) return

    setEjecutando(true)
    setProgreso({ indice: 0, total: objetivos.length })
    setMsg({ tipo: 'info', txt: `🔍 Escaneando ${objetivos.length} productos vía extensión…` })

    const pendientes = new Set()
    async function onItemListo(e) {
      if (e.source !== window) return
      const d = e.data || {}
      if (d.type === 'LIMPACK_ITEM_START') setProgreso({ indice: d.indice, total: d.total, nombre: d.nombre })
      if (d.type === 'LIMPACK_PROGRESS') setProgreso(p => p ? { ...p, phase: d.phase, count: d.count } : null)
      if (d.type === 'LIMPACK_ITEM_LISTO' && d.resultado) {
        const pr = supabase.functions.invoke('guardar-precios-producto', {
          body: { producto_id: d.item_id, resultado: d.resultado },
        }).catch(err => console.error('guardar-precios-producto fallo:', err))
        pendientes.add(pr); pr.finally(() => pendientes.delete(pr))
      }
      if (d.type === 'LIMPACK_BUSQUEDA_LISTA') {
        window.removeEventListener('message', onItemListo)
        await Promise.all([...pendientes])
        setMsg({ tipo: 'ok', txt: `✅ ${d.resultados?.length || objetivos.length} productos escaneados con datos reales de ML` })
        setSeleccion(new Set()); setProgreso(null); setEjecutando(false); cargar()
      }
      if (d.type === 'LIMPACK_BUSQUEDA_ERROR') {
        window.removeEventListener('message', onItemListo)
        setMsg({ tipo: 'err', txt: 'Error en extensión: ' + d.error })
        setProgreso(null); setEjecutando(false)
      }
    }
    window.addEventListener('message', onItemListo)
    window.postMessage({
      type: 'LIMPACK_BUSCAR_PRECIOS',
      payload: {
        productos: objetivos.map(p => ({
          item_id: p.id, marca: p.marca, nombre: p.nombre, presentacion: p.presentacion,
        })),
        portales: ['MercadoLibre'],
      },
    }, '*')
  }

  return (
    <AdminLayout>
      <div className="page">
        <div className="im-head">
          <div>
            <h1>📊 Inteligencia de mercado</h1>
            <p className="muted">Dónde estás parado en ML vs la competencia. Escaneo con tu extensión Chrome — sin gastar créditos ni API.</p>
          </div>
        </div>

        {/* Estado extensión */}
        <div className="im-ext" style={{
          background: extInstalada ? '#f0fdf4' : '#fef3c7',
          border: `1px solid ${extInstalada ? '#86efac' : '#fcd34d'}`,
          color: extInstalada ? '#166534' : '#78350f',
        }}>
          <span className="im-ext-dot" style={{ background: extInstalada ? '#22c55e' : '#f59e0b' }} />
          {extInstalada
            ? 'Extensión "Limpack — Precios" conectada'
            : 'Extensión "Limpack — Precios" no detectada — abrila/instalala para poder escanear (extension-precios/README.md)'}
        </div>

        {/* Bloque A */}
        <h2 className="im-block-title">A · Mis productos vs el mercado</h2>

        {/* Resumen */}
        <div className="im-resumen">
          <div className="im-tile"><div className="im-tile-lbl">Publicados</div><div className="im-tile-val">{resumen.total}</div></div>
          <div className="im-tile"><div className="im-tile-lbl">Escaneados</div><div className="im-tile-val">{resumen.escaneados}<span className="im-tile-sub"> / {resumen.total}</span></div></div>
          <div className="im-tile"><div className="im-tile-lbl">Competitivos</div><div className="im-tile-val" style={{ color: '#16A34A' }}>{resumen.competitivo}</div></div>
          <div className="im-tile"><div className="im-tile-lbl">En línea</div><div className="im-tile-val" style={{ color: '#3B82F6' }}>{resumen.en_linea}</div></div>
          <div className="im-tile"><div className="im-tile-lbl">Caros</div><div className="im-tile-val" style={{ color: '#F59E0B' }}>{resumen.caro + resumen.muy_caro}</div></div>
          <div className="im-tile"><div className="im-tile-lbl">Sin escanear</div><div className="im-tile-val" style={{ color: '#94A3B8' }}>{resumen.sin_escanear}</div></div>
        </div>

        {/* Acciones */}
        <div className="im-actions">
          <input type="search" className="im-search" placeholder="🔎 Buscar producto…"
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          <div style={{ flex: 1 }} />
          <button className="im-btn-ghost" disabled={ejecutando || !extInstalada}
            onClick={() => escanear(filtrados)}>
            Escanear todos ({filtrados.length})
          </button>
          <button className="im-btn-primary" disabled={ejecutando || !extInstalada || seleccion.size === 0}
            onClick={() => escanear(productos.filter(p => seleccion.has(p.id)))}>
            {ejecutando ? 'Escaneando…' : `Escanear selección (${seleccion.size})`}
          </button>
        </div>

        {progreso && (
          <div className="im-progreso">
            <div className="im-progreso-bar" style={{ width: `${Math.round(((progreso.indice || 0) / (progreso.total || 1)) * 100)}%` }} />
            <span className="im-progreso-txt">
              {progreso.indice || 0}/{progreso.total} · {progreso.nombre || '…'}{progreso.phase ? ` · ${progreso.phase}` : ''}
            </span>
          </div>
        )}

        {msg && (
          <div className={`im-msg im-msg-${msg.tipo}`}>{msg.txt}</div>
        )}

        {loading && <div className="im-loading">Cargando…</div>}

        {!loading && (
          <div className="im-table-wrap">
            <table className="im-table">
              <thead>
                <tr>
                  <th style={{ width: 30 }}>
                    <input type="checkbox" checked={filtrados.length > 0 && seleccion.size === filtrados.length}
                      onChange={toggleTodos} />
                  </th>
                  <th>Producto</th>
                  <th style={{ textAlign: 'right' }}>Mi precio</th>
                  <th style={{ textAlign: 'right' }}>Mercado ML</th>
                  <th style={{ textAlign: 'center' }}>Dif.</th>
                  <th style={{ textAlign: 'center' }}>Posición</th>
                  <th style={{ textAlign: 'center' }}>Compet.</th>
                  <th style={{ textAlign: 'right' }}>Escaneo</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(p => {
                  const st = p.ml_stats || {}
                  const pos = st.posicion || (p.ml_stats_actualizado_at ? 'sin_datos' : null)
                  const meta = pos ? (POSICION_META[pos] || POSICION_META.sin_datos) : null
                  const diff = st.diff_pct
                  const precioRef = st.precio_ref ?? p.precio_mercado
                  const cantidad = st.stats?.cantidad
                  const matchExacto = st.match_exacto?.precio
                  return (
                    <tr key={p.id} className={seleccion.has(p.id) ? 'im-row-sel' : ''}>
                      <td><input type="checkbox" checked={seleccion.has(p.id)} onChange={() => toggleSel(p.id)} /></td>
                      <td>
                        <div className="im-prod-nombre">{p.marca} · {p.nombre}</div>
                        <div className="im-prod-sub">{p.sku} · {p.presentacion} · {p.categoria}</div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(p.precio_publico)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {precioRef ? (
                          <>
                            <div>{fmt(precioRef)}</div>
                            {st.stats && (
                              <div className="im-prod-sub">
                                {matchExacto ? 'match exacto' : `mediana · ${cantidad || 0} publ.`}
                              </div>
                            )}
                          </>
                        ) : <span style={{ color: '#CBD5E1' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {diff == null ? <span style={{ color: '#CBD5E1' }}>—</span> : (
                          <span style={{ color: diff <= -5 ? '#16A34A' : diff <= 8 ? '#3B82F6' : diff <= 20 ? '#F59E0B' : '#EF4444' }}>
                            {diff >= 0 ? '+' : ''}{diff}%
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {meta ? (
                          <span className="im-pill" style={{ background: meta.bg, color: meta.color }} title={st.razon || ''}>
                            {meta.icon} {meta.label}
                          </span>
                        ) : <span className="im-pill" style={{ background: '#F8FAFC', color: '#94A3B8' }}>sin escanear</span>}
                      </td>
                      <td style={{ textAlign: 'center', fontSize: 13, color: '#64748B' }}>
                        {cantidad != null ? cantidad : '—'}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 11, color: '#94A3B8' }}>{fmtFechaRel(p.ml_stats_actualizado_at)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="im-nota">
          <strong>Cómo se lee:</strong> "Mi precio" es tu precio publicado. "Mercado ML" es el match exacto (misma marca+presentación) si lo encuentra, sino la mediana de las publicaciones similares. La diferencia y la posición te dicen si estás competitivo (verde), en línea (azul) o caro (amarillo/rojo). El escaneo usa tu extensión Chrome, no gasta créditos.
        </div>

        <div className="im-proximos">
          Próximos bloques: <strong>B · Categorías calientes</strong> (dónde hay más movimiento en ML) · <strong>C · Huecos de catálogo</strong> (productos de tus proveedores que no publicaste y tienen margen).
        </div>
      </div>

      <style>{`
        .page h1 { margin: 12px 0 4px; }
        .muted { color: #64748b; font-size: 13px; margin: 0 0 16px; }
        .im-head { display: flex; justify-content: space-between; align-items: flex-start; }

        .im-ext { display: flex; align-items: center; gap: 8px; padding: 9px 14px; border-radius: 10px; font-size: 12.5px; font-weight: 600; margin-bottom: 20px; }
        .im-ext-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

        .im-block-title { font-size: 15px; color: #1a3461; margin: 8px 0 12px; font-weight: 800; }

        .im-resumen { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 10px; margin-bottom: 16px; }
        .im-tile { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; }
        .im-tile-lbl { font-size: 10.5px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 4px; }
        .im-tile-val { font-size: 22px; font-weight: 800; color: #1a3461; font-variant-numeric: tabular-nums; line-height: 1; }
        .im-tile-sub { font-size: 12px; font-weight: 500; color: #94a3b8; }

        .im-actions { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
        .im-search { flex: 0 1 280px; padding: 9px 13px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 13.5px; font-family: inherit; }
        .im-search:focus { outline: none; border-color: #00A8B5; }
        .im-btn-primary, .im-btn-ghost { padding: 9px 16px; border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; white-space: nowrap; }
        .im-btn-primary { background: #1a3461; color: #fff; border: 0; }
        .im-btn-primary:hover:not(:disabled) { background: #15294d; }
        .im-btn-ghost { background: #fff; color: #475569; border: 1.5px solid #e2e8f0; }
        .im-btn-ghost:hover:not(:disabled) { border-color: #1a3461; color: #1a3461; }
        .im-btn-primary:disabled, .im-btn-ghost:disabled { opacity: .5; cursor: not-allowed; }

        .im-progreso { position: relative; height: 26px; background: #f1f5f9; border-radius: 8px; overflow: hidden; margin-bottom: 12px; }
        .im-progreso-bar { position: absolute; inset: 0 auto 0 0; background: linear-gradient(90deg, #1a3461, #00a8b5); transition: width .3s; }
        .im-progreso-txt { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 11.5px; font-weight: 700; color: #1a3461; }

        .im-msg { padding: 10px 14px; border-radius: 10px; font-size: 13px; margin-bottom: 12px; }
        .im-msg-ok { background: #dcfce7; color: #166534; }
        .im-msg-err { background: #fee2e2; color: #991b1b; }
        .im-msg-info { background: #dbeafe; color: #1e40af; }

        .im-loading { padding: 40px; text-align: center; color: #64748b; }
        .im-table-wrap { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow-x: auto; }
        .im-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .im-table thead th { background: #f8fafc; padding: 9px 12px; font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .03em; text-align: left; border-bottom: 1px solid #e5e7eb; white-space: nowrap; }
        .im-table tbody td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
        .im-table tbody tr:hover { background: #f8fafc; }
        .im-row-sel { background: #f0fdfa !important; }
        .im-prod-nombre { font-weight: 700; color: #1a3461; }
        .im-prod-sub { font-size: 10.5px; color: #94a3b8; margin-top: 2px; }
        .im-pill { display: inline-block; padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 800; white-space: nowrap; }

        .im-nota { margin-top: 14px; padding: 12px 16px; background: #F0FDFA; border-left: 3px solid #00A8B5; border-radius: 0 8px 8px 0; font-size: 12.5px; color: #134E4A; line-height: 1.5; }
        .im-proximos { margin-top: 10px; padding: 10px 14px; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 8px; font-size: 12px; color: #64748b; }
      `}</style>
    </AdminLayout>
  )
}
